/**
 * Permission codes the Legal Entities screens (FSD 1.2.3) declare an interest in.
 *
 * They are the dot-strings `server/internal/org/permissions_fnd.go` (§SP4)
 * registers with the RBAC catalogue (CLAUDE.md §6) and that arrive on
 * `/auth/me`. The wire path is `/api/v1/foundation/legal-entities` (FSD's own
 * naming) but the permission module stays `org` — the deviation recorded in
 * `DECISIONS.md` 2026-09-19: the record is physically `companies`/`CompanyID`
 * in Go and SQL, only this module's copy, routes and DTO field names say
 * "Legal Entity."
 *
 * 1.2.3.11 is explicit that "no single role can complete an entity end to
 * end": creation/dissolution needs the Legal Entity Owner role, ledger/
 * balancing assignment needs Finance Controller, PSU/Legal-Employer
 * designation needs Payroll Manager, and re-designating a PSU's Legislative
 * Data Group is its own privileged, dual-approved action carrying
 * `RequiresMFA` server-side — this module does not build the redesignation
 * screen (out of this packet's scope; a later packet owns PSU/TRU), but the
 * code is declared here so the permission catalogue this module depends on is
 * complete and any future screen finds it already named.
 */

export const LEGAL_ENTITY_PERMISSIONS = {
  read: 'org.legal_entity.read',
  create: 'org.legal_entity.create',
  update: 'org.legal_entity.update',
  dissolve: 'org.legal_entity.dissolve',
  submit: 'org.legal_entity.submit',

  /** Ledger mapping and balancing-segment assignment (F-7.001, F-7.009) — Finance Controller. */
  ledgerManage: 'org.legal_entity.ledger_manage',

  /** Legal Employer / PSU designation and the PSU/LDG binding (F-7.007, F-7.008) — Payroll Manager. */
  designationManage: 'org.legal_entity.designation_manage',

  /**
   * F-7.008's controlled Legislative Data Group re-designation event.
   * `RequiresMFA` server-side (`permissions_fnd.go`) — a guarded route replays
   * automatically once `src/api/stepup.ts`'s broker collects the code, so no
   * screen needs bespoke step-up handling for it.
   */
  psuRedesignate: 'org.legal_entity.psu_redesignate',

  /** 1.2.3.11's narrower grant — "excluded from general reporting scopes." */
  ownershipRead: 'org.legal_entity.ownership_read',
  ownershipManage: 'org.legal_entity.ownership_manage',

  /**
   * Gates the intercompany AND elimination rule LISTS (`GET
   * /foundation/intercompany-rules`, `GET /foundation/elimination-rules` in
   * `handler_le.go`). The two screens are routed on this code, so a read-only
   * holder sees both registers and none of the write actions.
   */
  intercompanyRead: 'org.legal_entity.intercompany_read',

  /**
   * Gates intercompany/elimination rule create AND the intercompany simulate
   * route — simulate is a manage-level call server-side, not a read.
   */
  intercompanyManage: 'org.legal_entity.intercompany_manage',
} as const

/**
 * Other modules' read codes the ownership/intercompany screens consume for
 * their pickers. Without them the picker falls back to a numeric id input —
 * the write itself is still the intercompany permission's call.
 */
export const LEGAL_ENTITY_FOREIGN_PERMISSIONS = {
  accountRead: 'org.account.read',
  consolGroupRead: 'consol.group.read',
} as const

export type LegalEntityPermission = (typeof LEGAL_ENTITY_PERMISSIONS)[keyof typeof LEGAL_ENTITY_PERMISSIONS]
